import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { JOURNEY_TYPES, getJourneyTypeFromData } from "../services/userJourneyDecisionService";

const STORAGE_KEY = "ht_user_journey";

const UserJourneyContext = createContext(null);

function readStored() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    console.warn("Failed to read user journey from sessionStorage", e);
    return null;
  }
}

export function UserJourneyProvider({ children }) {
  const [journeyType, setJourneyType] = useState(() => readStored()?.journeyType || "");
  const [decisionData, setDecisionData] = useState(() => readStored()?.decisionData || null);
  const [thanksErrorMessage, setThanksErrorMessage] = useState("");

  useEffect(() => {
    try {
      if (journeyType || decisionData) {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ journeyType, decisionData })
        );
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {
      console.warn("Failed to write user journey to sessionStorage", e);
    }
  }, [journeyType, decisionData]);

  const applyDecisionPayload = useCallback((data) => {
    const type = getJourneyTypeFromData(data);
    setJourneyType(type || "");
    setDecisionData(data && typeof data === "object" ? data : null);
  }, []);

  const clearJourney = useCallback(() => {
    setJourneyType("");
    setDecisionData(null);
  }, []);

  /** Force FLDG thanks generic copy (errors / missing redirect). */
  const setThanksError = useCallback((message) => {
    setThanksErrorMessage(message || "error");
  }, []);

  const clearThanksError = useCallback(() => {
    setThanksErrorMessage("");
  }, []);

  const isFldgJourney = journeyType === JOURNEY_TYPES.UBL_FLDG;

  const value = {
    journeyType,
    decisionData,
    isFldgJourney,
    thanksErrorMessage,
    applyDecisionPayload,
    clearJourney,
    setJourneyType,
    setThanksError,
    clearThanksError,
  };

  return (
    <UserJourneyContext.Provider value={value}>
      {children}
    </UserJourneyContext.Provider>
  );
}

export function useUserJourney() {
  const ctx = useContext(UserJourneyContext);
  if (!ctx) {
    throw new Error("useUserJourney must be used inside UserJourneyProvider");
  }
  return ctx;
}
